import React from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Player, Position } from '../../types';
import { DraggablePlayerRow } from './DraggablePlayerRow';
import { calculateNonLinearRating } from '../../utils/rankingCalculations';

interface PositionRankingListProps {
  players: Player[];
  position: Position;
  rankedPlayerIds: string[];
  onRankingChange: (newRankedPlayerIds: string[]) => void;
}

export function PositionRankingList({
  players,
  position,
  rankedPlayerIds,
  onRankingChange,
}: PositionRankingListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = rankedPlayerIds.indexOf(active.id as string);
      const newIndex = rankedPlayerIds.indexOf(over.id as string);

      const newRankedPlayerIds = arrayMove(rankedPlayerIds, oldIndex, newIndex);
      onRankingChange(newRankedPlayerIds);
    }
  };

  // Get position name for display
  const positionName = position === 'FWD'
    ? 'Forward'
    : position === 'MID'
    ? 'Midfielder'
    : position === 'DEF'
    ? 'Defender'
    : 'Goalkeeper';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Rank Players for {positionName} ({position})
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Drag players to rank them from best (#1) to worst (#{rankedPlayerIds.length}). Ratings are calculated automatically using non-linear distribution.
        </p>
      </div>

      {/* Sortable List */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={rankedPlayerIds}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-2">
            {rankedPlayerIds.map((playerId, index) => {
              const player = players.find((p) => p.id === playerId);
              if (!player) return null;

              const rank = index + 1;
              const previewRating = calculateNonLinearRating(rank, rankedPlayerIds.length);

              return (
                <DraggablePlayerRow
                  key={player.id}
                  player={player}
                  rank={rank}
                  position={position}
                  previewRating={previewRating}
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
